/**
 * Shared constants for the Hyperliquid speed-trading API.
 *
 * The speed-trading backend (`api.wolfswap.app/speed/*`) runs entirely on
 * HyperEVM (chain id 999). Every `/speed` HTTP and WebSocket route only accepts
 * `networkId = 999`. The user's wallet still connects on an "origin" chain
 * (Cronos by default) purely to fund deposits — that origin is per-request and
 * is NOT the trading network.
 */

/** HyperEVM network id. Every `/speed` endpoint and WS route requires this. */
export const SPEED_NETWORK_ID = 999;

/**
 * Default origin chain users bridge funds from when depositing. Cronos is the
 * first supported origin; deposit/withdraw still accept any Relay-supported
 * chain per request.
 */
export const DEFAULT_ORIGIN_CHAIN_ID = 25;

/**
 * USDC.e (bridged USDC, 6 decimals) on Cronos — the withdraw destination currency and one of
 * the two Cronos deposit origins. Cronos has TWO USDCs (see `CRONOS_USDC_NATIVE_ADDRESS`);
 * Relay accepts either as a deposit origin, so the deposit form offers whichever the user holds.
 */
export const CRONOS_USDC_ADDRESS = '0xc21223249ca28397b4b6541dffaecc539bff0c59';

/** Native (Circle-issued) USDC, 6 decimals, on Cronos — the other Cronos deposit origin. */
export const CRONOS_USDC_NATIVE_ADDRESS =
    '0x3d7f2c478aafdb65542bcb44bceec05849999d2d';

/**
 * USDG (6 decimals) on Robinhood Chain (4663): the stable Relay routes there, so
 * it is both the deposit origin token and the withdrawal destination currency
 * when the account is anchored on Robinhood.
 */
export const ROBINHOOD_USDG_ADDRESS =
    '0x5fc5360d0400a0fd4f2af552add042d716f1d168';

/** USDC is 6 decimals on every chain in this flow. */
export const USDC_DECIMALS = 6;

/** Default slippage (basis points) applied to opens when the user does not override it. */
export const DEFAULT_SLIPPAGE_BPS = 100;

/**
 * Backend error `code` (HTTP 422 from `/speed/trade/open|close`) meaning the
 * user's Hyperliquid agent was pruned/expired and must be re-approved before the
 * order can go through. The FE maps this to the "Reactivate perps wallet" banner.
 */
export const AGENT_REACTIVATION_REQUIRED_CODE = 'AGENT_REACTIVATION_REQUIRED';

/**
 * Backend error `code` (HTTP 422 from `/speed/trade/open`) meaning the account
 * still needs the one-time HIP-3 (unified-account) activation before its first
 * builder-market open. The FE maps this to the "Activate HIP-3 markets" banner.
 */
export const HIP3_ACTIVATION_REQUIRED_CODE = 'HIP3_ACTIVATION_REQUIRED';

/**
 * Backend error `code` (HTTP 422 from `/speed/trade/open`) meaning the requested margin exceeds
 * what is on HyperCore right now (the rest still sits on the HyperEVM custodial/adapter wallets,
 * which a fast open never moves). The order was NOT placed; the message names the tradeable amount.
 */
export const CORE_COLLATERAL_INSUFFICIENT_CODE = 'CORE_COLLATERAL_INSUFFICIENT';

/**
 * Challenge markers of the fund-authorization handshake on
 * `POST /speed/deposit|withdraw`. A strict request (one that carries a
 * `fundAuthorizations` map, even empty) that still needs user signatures is
 * answered with HTTP 200 and the body
 * `{ status: 'fundAuthorizationRequired', code: 'FUND_AUTHORIZATION_REQUIRED',
 * required: FundAuthorizationDescriptor[] }` — see
 * `fundAuthorizationRequiredResponse` in
 * `MoonHub.API/managers/SpeedTradingJobManager.ts`. The FE signs each
 * descriptor and repeats the SAME request with the `fundAuthorizations` map
 * keyed by the descriptors' `key` values. Trading never uses this handshake —
 * agent-mode orders go through a different signer path and must never prompt.
 */
export const FUND_AUTHORIZATION_REQUIRED_CODE = 'FUND_AUTHORIZATION_REQUIRED';
export const FUND_AUTHORIZATION_REQUIRED_STATUS = 'fundAuthorizationRequired';

/**
 * Client-side timeout for `POST /speed/agent/reactivate`. Reactivation runs a
 * service-wallet tx + `extraAgents` confirmation, normally well under a minute;
 * this overrides the default 30s client timeout with headroom for a slow chain.
 */
export const AGENT_REACTIVATE_TIMEOUT_MS = 60_000;

/**
 * Client-side timeout for `POST /speed/trade/ensure-triggers`. The endpoint
 * agent-signs and posts an `/exchange` trigger order per missing TP/SL leg, so it
 * can run well past the default 30s client timeout — override with headroom.
 */
export const ENSURE_TRIGGERS_TIMEOUT_MS = 120_000;

/**
 * Client-side timeout for `POST /speed/trade/update-triggers`. Editing a live
 * position's TP/SL agent-signs a cancel and/or a fresh `/exchange` trigger order
 * per touched leg, so it outlives the default 30s client timeout. Shorter than
 * {@link ENSURE_TRIGGERS_TIMEOUT_MS} because this is a user-initiated edit of a
 * SINGLE trade (at most two legs), not a sweep over every open trade — the user
 * is watching the button spin, so the failure should surface sooner.
 */
export const UPDATE_TRIGGERS_TIMEOUT_MS = 60_000;

/**
 * Client-side timeout for `POST /speed/trade/update-margin`. Adding margin to a
 * live position agent-signs ONE `updateIsolatedMargin` action after a balance
 * and position read, so it is shorter-lived than a TP/SL edit but still
 * outlives the default 30s client timeout on a slow signer turn.
 */
export const UPDATE_MARGIN_TIMEOUT_MS = 45_000;

/**
 * Client-side timeout for `POST /speed/trade/limit/{cancel,execute}`. A cancel
 * agent-signs the cancel and then READS the live position to decide whether the
 * order filled underneath it; "fill now" additionally runs a whole market open
 * after that cancel. Both outlive the default 30s client timeout.
 */
export const LIMIT_ORDER_TIMEOUT_MS = 60_000;

/**
 * Backend error `code` (HTTP 422 from `/speed/trade/limit/{cancel,execute}`)
 * meaning there is no Resting order for that pair — it filled, was cancelled,
 * or never existed. The FE refreshes the trade lists rather than retrying.
 */
export const NO_RESTING_ORDER_CODE = 'noRestingOrder';

/**
 * Backend error `code` (HTTP 422 from `/speed/trade/limit/{cancel,execute}`)
 * meaning the row still carries a `pending:` hash: the placement was ambiguous,
 * so the backend does not yet know the order id to cancel. The reconciler
 * adopts or fails the row within ~2 minutes; the user waits, nothing is lost.
 */
export const ORDER_UNCONFIRMED_CODE = 'orderUnconfirmed';

/**
 * Backend error `code` (HTTP 422 from `/speed/trade/limit/open`) meaning the
 * limit price was missing, non-positive or non-finite.
 */
export const BAD_LIMIT_PRICE_CODE = 'badLimitPrice';
